import React, { useEffect, useRef } from 'react'
import { useMsal } from '@azure/msal-react'
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom'
import Home from './pages/Home'
import MsalAuthenticatedRouter, {
  useIsAuthenticated,
  useIsShareholder,
  useLogout,
} from './features/azure-b2c/components/MsalRouter'
import Header from './components/layout/Header'
import { styled } from '@mui/material'
import {
  APPBAR_HEIGHT,
  BOTTOM_NAVIGATION_HEIGHT,
  DRAWER_DIMENSIONS,
  PAGE_PADDING_SIDE,
  PAGE_PADDING_TOP,
} from './util/consts'
import PastProducts from './pages/Products'
import { InteractionStatus } from '@azure/msal-browser'
import { Loader } from './components/Loader'
import { TestIds } from './testIds'
import NoAccess from './pages/NoAccess'
import SalesAgreements from './pages/SalesAgreements'
import SalesAgreement from './features/sales-agreements/SalesAgreement'
import { useScrollToTop } from './util/useScrollToTop'
import Cart from './pages/Cart'
import { useSelector } from 'react-redux'
import { selectCanUseQuickOrder } from './features/azure-b2c/storage'
import { CheckoutSuccess } from './pages/CheckoutSuccess'
import Orders from './pages/Orders'
import OrderDetails from './features/orders/OrderDetails'
import { RequestAQuoteSuccess } from './pages/RequestAQuoteSuccess'
import AddToCartProvider from './features/cart/components/useAddToCartModal'
import DrawdownProvider from './features/drawdown/useDrawdownRequest'
import { DrawdownSuccess } from './pages/DrawdownSuccess'
import ProductDetails from './features/products/pages/ProductDetails'
import { PreviousPageProvider } from './util/usePreviousPage'
import { PickUpStoreModalProvider } from './features/cart/components/PickUpStoreModal'
import { DeleteAccountSuccess } from './pages/DeleteAccountSuccess'
import { MyAccount } from './pages/MyAccount'
import { EnquiryRequestSent } from './pages/EnquiryRequestSent'
import { ProductNotFound } from './features/products/pages/ProductNotFound'
import { RemoteConfigProvider } from './services/firebase/remote-config'
import { SeedOrder } from './pages/SeedOrder'
import { SeedOrderSummary } from './features/seed-orders/SeedOrderSummary'
import { SeedOrderSuccess } from './pages/SeedOrderSuccess'
import PushNotificationsProvider from './services/push-notifications/PushNotificationsProvider'
import AtomicProvider from './services/atomic/AtomicProvider'
import { selectRemoteConfig } from './services/firebase/remote-config/remoteConfig.slice'
import { AccountSwitcherProvider } from './features/azure-b2c/components/AccountSwitcherProvider'
import QuoteDetails from './features/quotes/QuoteDetails'
import Quotes from './pages/Quotes'
import { QuoteOrderSubmitted } from './pages/QuoteOrderSubmitted'

const Logout = () => {
  const logout = useLogout()
  const { inProgress } = useMsal()

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      logout()
    }
  }, [inProgress])
  return <Loader />
}

const ResponseivePageBody = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: 'fit-content',
  minHeight: '100%',
  padding: `calc(${PAGE_PADDING_TOP.lg}px)  ${PAGE_PADDING_SIDE.lg}px`,

  [theme.breakpoints.down('lg')]: {
    padding: `calc(${PAGE_PADDING_TOP.md}px)  ${PAGE_PADDING_SIDE.md}px`,
  },

  [theme.breakpoints.down('md')]: {
    padding: `calc(${PAGE_PADDING_TOP.sm}px)  ${PAGE_PADDING_SIDE.sm}px`,
  },

  [theme.breakpoints.down('sm')]: {
    padding: `calc(${PAGE_PADDING_TOP.xs}px)  ${PAGE_PADDING_SIDE.xs}px`,
  },

  [theme.breakpoints.down('xs')]: {
    paddingBottom: `calc(${BOTTOM_NAVIGATION_HEIGHT}px + 16px)`,
  },
}))

const ResponsivePageLayout = styled('div')(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  overflow: 'scroll',
  overflowX: 'hidden',
  paddingLeft: `calc(min(20vw, ${DRAWER_DIMENSIONS.width.lg}px) + ${DRAWER_DIMENSIONS.margin.horizontal.lg}px)`,
  [theme.breakpoints.between('md', 'lg')]: {
    height: `calc(var(--doc-height) - ${DRAWER_DIMENSIONS.margin.vertical.md * 2}px)`,
  },
  [theme.breakpoints.down('md')]: {
    padding: `0 0 ${BOTTOM_NAVIGATION_HEIGHT}px 0`,
  },
}))

const PageLayout: React.FC<{ children: React.ReactNode }> = (props) => {
  return (
    <ResponsivePageLayout
      id={TestIds.pages.mainScrollContainer}
      data-testid={TestIds.pages.mainScrollContainer}
      className={TestIds.pages.mainScrollContainer}
      role='main'
    >
      <ResponseivePageBody>{props.children}</ResponseivePageBody>
    </ResponsivePageLayout>
  )
}

const Redirect: React.FC<{ path: string }> = ({ path }: { path: string }) => {
  const navigate = useNavigate()

  useEffect(() => {
    navigate(path)
  }, [])

  return <div />
}

const AppRoutes = () => {
  const isAuthenticated = useIsAuthenticated()
  const isShareholder = useIsShareholder()
  const canUseCart = useSelector(selectCanUseQuickOrder)
  const ref = useRef<HTMLElement>(null)
  const remoteConfig = useSelector(selectRemoteConfig)
  useScrollToTop({ ref })

  const routes = React.useMemo(() => {
    if (isShareholder)
      return (
        <Routes>
          <Route path='/logout' element={<Logout />} />
          <Route path='/home/*' element={<Home />} />
          <Route path='/products' element={<PastProducts />} />
          <Route path='/products/not-found' element={<ProductNotFound />} />
          <Route path='/products/:masterProductId' element={<ProductDetails />} />
          <Route path='/enquiry-request-sent' element={<EnquiryRequestSent />} />
          {canUseCart && <Route path='/products/cart' element={<Cart />} />}
          {canUseCart && (
            <Route path='/products/checkout/:confirmationId' element={<CheckoutSuccess />} />
          )}
          <Route
            path='/seed'
            element={remoteConfig.seedEnabled ? <SeedOrder /> : <Redirect path='/home' />}
          />
          {remoteConfig.seedEnabled && (
            <>
              <Route
                path='/seed/summary'
                element={canUseCart ? <SeedOrderSummary /> : <Redirect path='/home' />}
              />
              <Route
                path='/seed/success'
                element={canUseCart ? <SeedOrderSuccess /> : <Redirect path='/home' />}
              />
            </>
          )}

          <Route
            path='/request-quote-success'
            element={
              remoteConfig.quoteEnabled ? <RequestAQuoteSuccess /> : <Redirect path='/home' />
            }
          />
          <Route
            path='/quotes'
            element={remoteConfig.quoteEnabled ? <Quotes /> : <Redirect path='/home' />}
          />
          <Route
            path='/quotes/:quoteId'
            element={remoteConfig.quoteEnabled ? <QuoteDetails /> : <Redirect path='/home' />}
          />
          <Route
            path='/quote-order-submitted'
            element={
              remoteConfig.quoteEnabled ? <QuoteOrderSubmitted /> : <Redirect path='/home' />
            }
          />

          <Route path='/agreements' element={<SalesAgreements />} />
          <Route path='/agreement/:salesAgreementId' element={<SalesAgreement />} />
          <Route path='/agreement/:salesAgreementId/drawdown' element={<DrawdownSuccess />} />

          <Route path='/orders' element={<Orders />} />
          <Route path='/order/:orderId' element={<OrderDetails />} />
          <Route path='/account' element={<MyAccount />} />
          <Route path='/account/delete' element={<DeleteAccountSuccess />} />
          <Route path='/' element={<Redirect path='/home' />} />
          <Route path='' element={<Redirect path='/home' />} />
        </Routes>
      )
    else if (!isAuthenticated)
      return (
        <Routes>
          <Route path='/' element={<Loader />} />
          <Route path='/logout' element={<Logout />} />
        </Routes>
      )

    if (!isShareholder)
      return (
        <Routes>
          <Route path='*' element={<NoAccess />} />
          <Route path='/logout' element={<Logout />} />
        </Routes>
      )

    return <></>
  }, [isAuthenticated, isShareholder, canUseCart, remoteConfig.seedEnabled])

  return <PageLayout>{routes}</PageLayout>
}

const AppRouter = () => {
  return (
    <BrowserRouter>
      <PreviousPageProvider>
        <RemoteConfigProvider>
          <MsalAuthenticatedRouter
            element={
              <PushNotificationsProvider>
                <AccountSwitcherProvider>
                  <AtomicProvider>
                    <PickUpStoreModalProvider>
                      <AddToCartProvider>
                        <DrawdownProvider>
                          <Header appbarHeight={APPBAR_HEIGHT} />
                          <AppRoutes />
                        </DrawdownProvider>
                      </AddToCartProvider>
                    </PickUpStoreModalProvider>
                  </AtomicProvider>
                </AccountSwitcherProvider>
              </PushNotificationsProvider>
            }
          />
        </RemoteConfigProvider>
      </PreviousPageProvider>
    </BrowserRouter>
  )
}

export default AppRouter
